import { twJoin, twMerge } from '@utils/twMerge';
import { ChevronIcon } from '@dynamika-shared/ui/icons/Chevron';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { NeedHelp } from '../NeedHelp/NeedHelp';
import {
  BigLogo,
  IAGLogo,
  IAGLogoColourfull,
  SmallLogo,
} from '@dynamika-shared/ui/icons';
import { Menu } from './Menu/Menu';
import { Link } from 'react-router-dom';
import { routesPath } from '@router/routes';
import { useState } from 'react';
import { useSettingsSlice } from '@restore/settings/settings.slice';
import { getEnvVariables } from 'src/env';
import { TypeformSurveyInjector } from '@components/TypeformSurvey/TypeformSurveyInjector';

type SidebarProps = {
  MenuComponent?: React.FunctionComponent<{
    sidebarCollapsed: boolean | undefined;
  }>;
};

export const Sidebar: React.FC<SidebarProps> = ({ MenuComponent = Menu }) => {
  const [sidebarCollapsed, businessVersion, toggleSidebarCollapsed] =
    useSettingsSlice((state, actions) => [
      state.sidebarCollapsed,
      state.businessVersion,
      actions.toggleSidebarCollapsed,
    ]);
  const [logoHovered, setLogoHovered] = useState<boolean>();
  return (
    <div
      data-testid='Sidebar'
      className={twJoin(
        'flex h-full flex-col pb-2 transition-all duration-300',
        ' bg-white',
        sidebarCollapsed ? 'w-[8.8rem]' : 'w-[24rem]'
      )}
    >
      <div className=''>
        <div
          data-testid='sidebar-logo'
          className={twJoin(
            'relative  border-b px-2 py-2',
            sidebarCollapsed && 'flex items-center justify-center'
          )}
        >
          <Button
            onClick={() => toggleSidebarCollapsed()}
            data-testid='sidebar-toggle'
            icon={
              <ChevronIcon
                dir={sidebarCollapsed ? 'right' : 'left'}
                className='h-1.5 w-1.5 group-hover:text-white'
              />
            }
            className={twJoin([
              'absolute -bottom-[1.2rem] -right-[1.2rem] !min-h-[2.5rem] !h-[2.5rem] !w-[2.5rem] !bg-white hover:!bg-dodgerBlue',
              'border-brightGray border',
              'flex items-center justify-center',
              'shadow-sm group',
            ])}
          />
          <Link data-testid='Logo' to={routesPath.home}>
            {sidebarCollapsed ? (
              <SmallLogo className='h-[4.8rem] w-[4.8rem]' />
            ) : (
              <BigLogo className='h-[4.8rem] w-[18.4rem]' />
            )}
          </Link>
        </div>
      </div>

      <div className='overflow-y-auto justify-between h-full flex flex-col'>
        <div className='px-2 overflow-y-auto'>
          {MenuComponent && (
            <MenuComponent sidebarCollapsed={sidebarCollapsed} />
          )}
        </div>

        <div className='mt-2 justify-self-end'>
          <TypeformSurveyInjector
            apiKey={getEnvVariables().TYPEFORM_SURVEY_API_KEY}
          />
          <div data-testid='NeedHelp' className='flex justify-center'>
            <NeedHelp collapsed={sidebarCollapsed} />
          </div>

          <div className={twJoin('mt-2 ', !sidebarCollapsed && 'px-2')}>
            <div
              data-testid='IAG-link'
              role='button'
              onClick={() => open('https://www.ia-grp.com/', '_blank')}
              onMouseLeave={() => setLogoHovered(false)}
              onMouseEnter={() => setLogoHovered(true)}
              className={twMerge(
                'flex cursor-pointer items-center justify-between py-1 pl-1.5 pr-2 hover:bg-waterLightHover50 rounded-2xl',
                sidebarCollapsed && 'mx-[1rem] px-[4px]'
              )}
            >
              <div>
                {logoHovered ? (
                  <IAGLogoColourfull className='h-[3.2rem] w-[6rem]' />
                ) : (
                  <IAGLogo className='h-[3.2rem] w-[6rem]' />
                )}
              </div>
              {!sidebarCollapsed && (
                <div className=''>
                  <ChevronIcon
                    dir='right'
                    className='h-2 w-2 text-spanishGray'
                  />
                </div>
              )}
            </div>
            {businessVersion && (
              <div className='px-2 py-1 text-1016 text-dimGray'>
                {businessVersion}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
